/**
 * "How to read this memo" (DOP-616): every mark the memo uses, in words.
 * Colour alone never carries a meaning on the memo page, so each colour and
 * mark is named here once. It sits folded under the section list, so it
 * takes no width from the memo; it opens on a reader's first visit.
 */

import { ChevronRight, Lock } from "lucide-react";
import React, { useState } from "react";

export interface MemoLegendProps {
	/** Why Mark reviewed is not available yet, shown in words; null when it is. */
	reviewBlockedReason: string | null;
}

const Row: React.FC<{ mark: React.ReactNode; children: React.ReactNode }> = ({ mark, children }) => (
	<li className="flex items-start gap-2.5 text-[12.5px] leading-[1.45] text-[#3f4247]">
		{/* Sized to its mark, not stretched to the row: an underline drawn as a
		    border would otherwise sit under the row's LAST line of text. */}
		<span className="flex h-[18px] w-[40px] shrink-0 items-center justify-center">{mark}</span>
		<span>{children}</span>
	</li>
);

const SEEN_KEY = "doppler.dealScreening.legendSeen";

const firstVisit = (): boolean => {
	try {
		if (window.localStorage.getItem(SEEN_KEY)) return false;
		window.localStorage.setItem(SEEN_KEY, "1");
		return true;
	} catch {
		return false;
	}
};

const MemoLegend: React.FC<MemoLegendProps> = ({ reviewBlockedReason }) => {
	const [open, setOpen] = useState(firstVisit);
	return (
	<aside aria-label="How to read this memo" className="space-y-3">
		<div className="rounded-xl border border-[#e7e7e4] bg-white">
			<button
				type="button"
				onClick={() => setOpen((was) => !was)}
				aria-expanded={open}
				className="flex w-full items-center gap-1.5 px-3 py-2.5 text-left text-[12.5px] font-semibold text-[#3f4247]"
			>
				<ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} aria-hidden />
				How to read this memo
			</button>
			{open && (
			<ul className="space-y-3 px-3 pb-3">
				<Row
					mark={
						<span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-[#E6F3F3] px-1 text-[10px] font-semibold text-[#004C4B]">
							4
						</span>
					}
				>
					Source number. Click it to open the page it came from.
				</Row>
				<Row mark={<span className="text-[11px] font-semibold text-[#111111] underline decoration-[#004C4B] decoration-1 underline-offset-2">$13.2</span>}>
					An underlined number opens its own page or cell.
				</Row>
				<Row mark={<span className="text-[11px] font-semibold text-[#b54708] underline decoration-[#b54708] decoration-1 underline-offset-2">$9.1</span>}>
					Two files disagree. Click to choose which one the memo uses.
				</Row>
				<Row mark={<span className="h-4 w-[22px] rounded border border-[#d9d0fb] bg-[#f4f0ff]" />}>
					Your own edit or answer. Kept when the memo is re-run.
				</Row>
				<Row mark={<span className="h-4 w-[22px] rounded bg-amber-50 ring-1 ring-amber-200" />}>
					No verified source. Check it before relying on it.
				</Row>
				<Row mark={<span className="text-[11px] font-bold text-[#004C4B]">↗</span>}>
					Not in the files, found on the web. Opens the page.
				</Row>
			</ul>
			)}
		</div>
		{reviewBlockedReason && (
			<p className="rounded-xl border border-dashed border-[#c9ccd1] px-3.5 py-3 text-[12.5px] leading-relaxed text-[#5B6472]">
				<Lock className="mr-1 inline h-3.5 w-3.5 -translate-y-px" aria-hidden />
				<b className="text-[#3f4247]">Mark reviewed</b> is locked. {reviewBlockedReason}.
			</p>
		)}
	</aside>
	);
};

export default MemoLegend;
